import { useRef, useState } from 'react';
import { signInWithEmailAndPassword, OAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { Link } from 'react-router-dom';
import AppBar from '../../components/AppBar';
import { GlobalInput } from '../../components/GlobalInput';
import { GlobalButton } from '../../components/GlobalButton';
import { GlobalMicroButton } from '../../components/GlobalMicroButton';
import styles from './page.module.css';
import { Toast } from '../../components/Toast';
import ContentGrid from '../../components/ContentGrid';
import AppFooter from '../../components/AppFooter';
import { useLocale } from '../../context/DynamicLocaleProvider';
import { AVAILABLE_LANGUAGES, LANGUAGE_TO_FLAG_MAP, LANGUAGE_NAMES } from '../../utils/languages';
import { getAuthErrorMessage } from '../../utils/authErrors';

export default function LoginPage() {
  const { t, locale } = useLocale();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const selectRef = useRef<HTMLSelectElement | null>(null);

  const handleLanguagePick = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    try {
      localStorage.setItem('language', value);
    } catch {}
    // Reload to ensure provider re-reads localStorage override immediately
    window.location.reload();
  };
  const openNativeLanguagePicker = () => {
    const sel = selectRef.current;
    if (!sel) return;
    try {
      sel.focus();
      sel.click();
    } catch {}
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(undefined);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      setShowToast(true);
    } catch (err) {
      const message = getAuthErrorMessage(err, t, 'login');
      setError(message ?? t('login_unexpected_error'));
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setIsMicrosoftLoading(true);
    setError(undefined);

    try {
      const provider = new OAuthProvider('microsoft.com');
      // Request email scope to get user's email domain
      provider.addScope('email');
      provider.addScope('profile');
      
      // Use popup for better UX - stays on same page
      await signInWithPopup(auth, provider);
      // User is now signed in, auth state change will handle navigation
      setShowToast(true);
      setIsMicrosoftLoading(false);
    } catch (err) {
      setIsMicrosoftLoading(false);
      const message = getAuthErrorMessage(err, t, 'login');
      if (message == null) return; // Popup closed, don't show error
      setError(message);
      setShowToast(true);
    }
  };

  return (
    <div>

      {/* App bar */}
      <AppBar />

      {/* Content */}
      <ContentGrid rows="auto 484px auto 48px">

        {/* Spacer */}
        <div style={{ gridRow: 1 }} />

        {/* Form container */}
        <div className={styles.formContainer}>

          {/* Language switcher */}
          <GlobalMicroButton
            type="button"
            onClick={openNativeLanguagePicker}
            className={styles.languageButton}
            icon={
              <img
                src={`https://cdn.ipregistry.co/flags/emojitwo/${LANGUAGE_TO_FLAG_MAP[locale] || 'de'}.svg`}
                alt="Language Icon"
                width={16}
                height={16}
              />
            }
          >
            {LANGUAGE_NAMES[locale] || (locale?.toUpperCase?.() || 'DE')}
          </GlobalMicroButton>

          {/* Hidden native select to open system dropdown */}
          <select
            ref={selectRef}
            aria-label="Select language"
            defaultValue={locale}
            onChange={handleLanguagePick}
            className={styles.languageSelectOverlay}
          >
            {AVAILABLE_LANGUAGES.map(lang => (
              <option key={lang.code} value={lang.code}>
                {lang.name}
              </option>
            ))}
          </select>

          {/* Headline & paragraph */}
          <h1 className={styles.title}>{t('login_welcome_back')}</h1>
          <p className={styles.subtitle}>{t('login_subtitle')}</p>

          {/* Form */}
          <form onSubmit={handleLogin} className={styles.form}>

            {/* E-mail */}
            <GlobalInput
                label={t('login_email_label')}
              type="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              required
              error={error}
            />

            {/* Password */}
            <GlobalInput
                label={t('login_password_label')}
              type="password"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              required
            />

            {/* Button */}
            <div className={styles.buttonContainer}>
              <GlobalButton
                type="submit"
                isLoading={isLoading}
                disabled={isLoading || isMicrosoftLoading}
              >
                {t('login_button')}
              </GlobalButton>

              <div className={styles.securityBadge}>
                <img src="/assets/icons/shield.lefthalf.filled.green.svg" alt="Shield" height={12} width={12} />
                <span>{t('login_encrypted_badge')}</span>
              </div>
            </div>

            <p className={styles.resetPasswordText}>
              {t('login_forgot_password')}{' '}
              <Link to="/reset-password" className={styles.link}>
                {t('login_reset_it')}
              </Link>
            </p>

            {/* Divider */}
            <div className={styles.divider}>
              <span>{t('login_or') || 'or'}</span>
            </div>

            {/* Microsoft SSO Button */}
            <GlobalButton
              type="button"
              onClick={handleMicrosoftLogin}
              isLoading={isMicrosoftLoading}
              disabled={isLoading || isMicrosoftLoading}
              variant="secondary"
              className={styles.microsoftButton}
              icon={
                <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0.5" y="0.5" width="9.5" height="9.5" fill="#F25022"/>
                  <rect x="10.5" y="0.5" width="9.5" height="9.5" fill="#7FBA00"/>
                  <rect x="0.5" y="10.5" width="9.5" height="9.5" fill="#00A4EF"/>
                  <rect x="10.5" y="10.5" width="9.5" height="9.5" fill="#FFB900"/>
                </svg>
              }
            >
              {isMicrosoftLoading ? (t('login_microsoft_loading') || 'Signing in...') : (t('login_microsoft_button') || 'Continue with Microsoft')}
            </GlobalButton>

            {/* Sign-up alternative 
            <p className={styles.signupText}>
              {t('login_dont_have_account')}{' '}
              <Link to="/signup" className={styles.link}>
                {t('login_sign_up')}
              </Link>
            </p>*/}
          </form>

          <Toast
            message={error ? error : t('login_success_message')}
            type={error ? 'error' : 'success'}
            isVisible={showToast}
            onClose={() => setShowToast(false)}
          />
        </div>

        {/* Spacer */}
        <div style={{ gridRow: 3 }} />

        {/* Footer */}
        <AppFooter inline />
      </ContentGrid>
    </div>
  );
}
